import React from 'react';
import { Loader2Icon, CheckCircle2Icon, XCircleIcon } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Card } from '../ui/card';

interface CreateMultiSourceStagingNodeToolCallProps {
  toolName: string;
  toolCallId: string;
  args: any;
  argsText: string;
  result?: string;
  status?: { type: 'running' | 'complete' | 'incomplete'; reason?: string };
}

export const CreateMultiSourceStagingNodeToolCall: React.FC<CreateMultiSourceStagingNodeToolCallProps> = ({
  args,
  argsText,
  result,
  status,
}) => {
  const isRunning = status?.type === 'running' || (status === undefined && result === undefined);
  const isError = status?.type === 'incomplete' && status.reason === 'error';
  const isComplete = !isRunning && !isError && result !== undefined;

  // Parse node name from args
  let nodeName = 'staging node';
  try {
    const parsedArgs = typeof args === 'object' ? args : JSON.parse(argsText);
    nodeName = parsedArgs?.name || parsedArgs?.node_name || 'staging node';
  } catch (e) {
    // Ignore parse errors
  }

  // Check if the result indicates success
  let isSuccess = false;
  if (isComplete && result) {
    try {
      const parsed = typeof result === 'string' ? JSON.parse(result) : result;
      isSuccess = parsed?.success === true;
    } catch (e) {
      isSuccess = !result.toLowerCase().includes('error');
    }
  }

  const hasError = isError || (isComplete && !isSuccess);

  return (
    <Card>
      <div className={cn(
        "p-3",
        isRunning
          ? "bg-primary/10 border-b border-primary/20"
          : "bg-muted/50 border-b border-border"
      )}>
        <div className="flex items-center gap-3">
          <div className="flex-1 space-y-1">
            {isRunning ? (
              <div className="flex items-center gap-2">
                <Loader2Icon className="h-4 w-4 text-primary animate-spin flex-shrink-0" />
                <p className="text-sm m-0 text-foreground">
                  Creating multi-source staging node: <strong>{nodeName}</strong>
                </p>
              </div>
            ) : hasError ? (
              <div className="space-y-1">
                <div className="flex items-center gap-2">
                  <div className="h-4 w-4 flex-shrink-0" />
                  <p className="text-sm m-0 text-muted-foreground">
                    Creating multi-source staging node: <strong>{nodeName}</strong>
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <XCircleIcon className="h-4 w-4 text-destructive flex-shrink-0" />
                  <p className="text-sm m-0 text-destructive">
                    Failed to create staging node
                  </p>
                </div>
              </div>
            ) : (
              <div className="space-y-1">
                <div className="flex items-center gap-2">
                  <div className="h-4 w-4 flex-shrink-0" />
                  <p className="text-sm m-0 text-muted-foreground">
                    Creating multi-source staging node: <strong>{nodeName}</strong>
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle2Icon className="h-4 w-4 text-success flex-shrink-0" />
                  <p className="text-sm m-0 text-foreground">
                    Staging node created successfully
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </Card>
  );
};
